/* FSK shared scroll-motion driver.
 *
 * Why JS and not native CSS scroll-timeline: native `animation-timeline` with a named
 * view-timeline failed to animate in a real supporting browser (2026-07-21), and gives
 * zero motion on iOS Safari / Firefox regardless. This drives the SAME CSS keyframes via
 * the paused-animation + negative-`animation-delay` technique, keyed to scroll.
 *
 * CRITICAL (2026-07-21): Horizon scrolls `.page-wrapper` above 990px and the document
 * below it — a `window` scroll listener NEVER fires on desktop, which left every scene
 * frozen (bio vesicles stuck invisible = "no bubbles"). So, exactly like fsk-haze.js, we
 * run a continuous requestAnimationFrame loop and read the live scrollTop from whichever
 * element actually scrolls, rather than relying on scroll events. getBoundingClientRect is
 * viewport-relative regardless of which element scrolls, so the per-element math is unchanged.
 *
 * Opt-in contract (progressive enhancement):
 *   - Scene root carries `data-fsk-scroll` (the element whose viewport crossing drives it).
 *   - Each animated element carries `data-r="<start> <end>"` (0..1 of the scene's view-progress).
 *   - CSS assigns `animation-name` under `.fsk-motion-js` (added only after the first frame).
 *   - Base CSS (outside `.fsk-motion-js`) MUST be the finished/visible frame so no-JS and
 *     reduced-motion fall through to it.
 */
(function () {
  'use strict';
  try {
    var rmq = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (rmq && rmq.matches) return; // respect reduced motion: leave the base finished frame

    var root = document.documentElement;
    var scenes = [];
    function clamp(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }

    function collect() {
      var nodes = document.querySelectorAll('[data-fsk-scroll]');
      scenes = [];
      for (var i = 0; i < nodes.length; i++) {
        var s = nodes[i];
        var animated = s.querySelectorAll('[data-r]');
        var els = [];
        for (var j = 0; j < animated.length; j++) {
          var parts = (animated[j].getAttribute('data-r') || '0 1').split(/\s+/);
          // data-follow="<selector>": ride the leading edge (tip) of the referenced path as it
          // draws, instead of running a keyframe. Used by the refill-loop arrowhead.
          var fsel = animated[j].getAttribute('data-follow');
          els.push({
            el: animated[j], a: parseFloat(parts[0]) || 0, b: parseFloat(parts[1]) || 1,
            follow: fsel ? s.querySelector(fsel) : null
          });
        }
        scenes.push({ root: s, els: els });
      }
    }

    function apply() {
      var vh = window.innerHeight;
      for (var i = 0; i < scenes.length; i++) {
        var sc = scenes[i];
        var r = sc.root.getBoundingClientRect();
        // view-progress: 0 as the scene's top enters the viewport bottom, 1 as its bottom exits the top
        var p = clamp((vh - r.top) / (vh + r.height));
        for (var j = 0; j < sc.els.length; j++) {
          var e = sc.els[j];
          var lp = e.b === e.a ? (p >= e.a ? 1 : 0) : clamp((p - e.a) / (e.b - e.a));
          if (e.follow) {
            // position + rotate the element at the tip of the drawing path (arrowhead).
            // Tangent is sampled by STRADDLING the tip (d-eps .. d+eps) so it stays correct at
            // the path endpoint — a one-sided sample collapses to zero-length there and the angle
            // wrongly defaults to 0deg, tilting the arrowhead off the curve.
            var L = e.follow.getTotalLength();
            var d = lp * L;
            var pt = e.follow.getPointAtLength(d);
            var pa = e.follow.getPointAtLength(Math.max(0, d - 1.5));
            var pc = e.follow.getPointAtLength(Math.min(L, d + 1.5));
            var ang = Math.atan2(pc.y - pa.y, pc.x - pa.x) * 180 / Math.PI;
            e.el.setAttribute('transform', 'translate(' + pt.x.toFixed(2) + ' ' + pt.y.toFixed(2) + ') rotate(' + ang.toFixed(1) + ')');
          } else {
            e.el.style.animationDelay = (-lp) + 's';
          }
        }
      }
    }

    // Read the live scroll offset from whichever element Horizon actually scrolls.
    function scrollTop() {
      var pw = document.querySelector('.page-wrapper');
      if (pw && pw.scrollHeight > pw.clientHeight + 4) return pw.scrollTop;
      return window.pageYOffset || root.scrollTop || (document.body ? document.body.scrollTop : 0) || 0;
    }

    var last = -1;
    function loop() {
      var t = scrollTop();
      if (t !== last) { last = t; apply(); }
      requestAnimationFrame(loop);
    }

    function init() {
      collect();
      apply();                             // set correct delays first...
      root.classList.add('fsk-motion-js');  // ...THEN activate the gate, so a failure can't blank anything
    }

    if (document.readyState !== 'loading') init();
    else document.addEventListener('DOMContentLoaded', init);
    requestAnimationFrame(loop);
    window.addEventListener('resize', apply, { passive: true });
    document.addEventListener('shopify:section:load', init);   // theme-editor re-render
    document.addEventListener('shopify:section:unload', collect);
  } catch (e) {}
})();
